import React from 'react'
import type { SelectAsyncCreatableProps } from '@ztechapps/webui.input'
import { SelectAsync, createRightAnchorMenuStyles } from '@ztechapps/webui.input'
import { HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { ProductStatusEnum } from '@ztechapps/common.contract'
import { OptionSimple } from './OptionSimple'
import { useProductSelect } from './useProductSelect'
import { FilterTypeButton } from './FilterTypeButton'
import { ProductSelectTypeFilter } from './ProductSelectTypeFilter'
import type { ProductSelectProps } from '@ztechapps/webui.contract'
import { ProductSelectSearchType } from '@ztechapps/webui.contract'

export const ProductSelect = ({
  filter: filterProps,
  defaultProductType = 'All',
  compact = false,
  As,
  ...rest
}: ProductSelectProps) => {
  const [filterType, setFilterType] = React.useState<ProductSelectSearchType>(ProductSelectSearchType.PartialSearch)
  const {
    dataLoadOptionsPartial,
    dataLoadOptionsText,
    selectKey,
    selectedProductType,
    setSelectedProductType,
    filter,
  } = useProductSelect({
    defaultProductType,
    filter: filterProps,
  })

  const selectDefaultProps: Partial<SelectAsyncCreatableProps> = {
    placeholder: `Type product code or name and press 'Enter' to add it.`,
    components: { Option: OptionSimple },
    labelKey: 'code',
    noOptionsMessage: () => 'Cannot find any products.',
    menuPortalTarget: document.body,
    menuPosition: 'fixed',
    // `libs/webui/input/src/Select/index.tsx`'s `createRightAnchorMenuStyles` — the shared
    // right-anchor fix; `menuBase` is only this component's own per-menu need (`max-content`
    // width with a 600px floor for its wide result rows). `950`: this menu is wider than the
    // shared default threshold assumes — long product descriptions need the extra room.
    styles: createRightAnchorMenuStyles(true, (base) => ({ ...base, width: 'max-content', minWidth: 600 }), 950),
    getNewOptionData: (inputValue, optionLabel) => {
      return {
        value: inputValue,
        label: inputValue,
        code: inputValue,
        status: ProductStatusEnum.Active,
        __isCreate: true,
        optionLabel,
      }
    },
    formatCreateLabel: (inputValue) => {
      return `Press TAB to create product "${inputValue}"`
    },
    ...rest,
  }
  const SelectComponent = As || SelectAsync

  return (
    <HorizontalStack>
      <div className="flex-fill">
        <ShowIf condition={filterType === 1}>
          <SelectComponent
            key={selectKey}
            {...selectDefaultProps}
            dataLoadOptions={dataLoadOptionsPartial({ filter })}
          />
        </ShowIf>
        <ShowIf condition={filterType !== 1}>
          <SelectComponent key={selectKey} {...selectDefaultProps} dataLoadOptions={dataLoadOptionsText({ filter })} />
        </ShowIf>
      </div>
      <ShowIf condition={!compact}>
        <FilterTypeButton {...{ filterType, setFilterType }} />
        <ProductSelectTypeFilter {...{ selectedProductType, setSelectedProductType }} />
      </ShowIf>
    </HorizontalStack>
  )
}
